import React, { useState, useRef, useEffect, useCallback } from "react";
import TpRectangleCard from "./components/TpRectangleCard";
import NavBar from "./components/NavBar";
import noResultsImage from "../../assets/images/PediaSearch.svg";
import { FaHeart, FaSearch, FaFile, FaTimes } from "react-icons/fa";
import { IoPlay } from "react-icons/io5";
import { GoArrowUpLeft } from "react-icons/go";
import { PiCardsThreeFill, PiHeadphonesFill } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import {
  bookmarkContent,
  getContentCounts,
  getSuggestions,
  likeContent,
  searchContent,
  getLikeBookmarkedContent,
  storeSearchTerm,
  getSearchHistory,
} from "./service";
import useStore from "../../store/useStore";
import { ContentItem } from "../../utils/contentMappers";
import { getDecodedToken, getLocalStorage } from "../../utils/localStorage";
import { AUTH_TOKEN } from "../../utils/constants";
import { getLabel } from "../../utils/appConfig";
import { getAllModuleData } from "./utils/helperFunctions";
import debounce from "lodash/debounce";
import { consumePediaSearchOrigin, trackZymedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

interface Suggestion {
  id: string;
  title: string;
  // add other fields as per API response
}

// Update the interface for content counts
interface ContentCounts {
  [key: string]: {
    isExternalSharable: number;
    like_count: number;
    uploadThumbnailUrl: string;
    previewUrl: string;
    readTime: string;
    duration: string;
  };
}

function PediaSearch() {
  // Get module data and add "Latest" as the first option
  const moduleData = getAllModuleData();
  const tabsData = [
    { id: "latest", code: "Latest", displayName: "Latest", pmEnabled: false },
    ...moduleData
  ];

  const [activeTab, setActiveTab] = useState<string>("latest");
  const tabsRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [searchResults, setSearchResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [localLikes, setLocalLikes] = useState<number[]>([]);
  const [localBookmarks, setLocalBookmarks] = useState<number[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [contentCounts, setContentCounts] = useState<ContentCounts>({});
  const { tatvaPediaToken } = useStore();
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchHistory, setSearchHistory] = useState<any>([]);
  const [showSearchHistory, setShowSearchHistory] = useState(true);

  const {
    tatvaPediaBookmarks,
    updateTatvaPediaBookmark,
    updateTatvaPediaLike,
    setTatvaPediaBookmarkedList,
    setTatvaPediaLikedList,
  } = useStore();

  // Add a ref to track pending operations
  const pendingOperations = useRef<{ [key: string]: boolean }>({});

  // Add this state at the top with other states
  const [showNoResults, setShowNoResults] = useState(false);
  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));

  // Add new state to track if content should be shown
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (getLocalStorage(AUTH_TOKEN)) {
      const decodedToken = getDecodedToken();
      setDoctorDetails({ ...doctorDetails, ...decodedToken });
    }
  }, []);

  useEffect(() => {
    fetchSearchHistory();
  }, [doctorDetails?.user?.b2cId]);

  // Add effect to fetch initial liked and bookmarked content
  useEffect(() => {
    const fetchInitialState = async () => {
      if (!tatvaPediaToken) return;

      try {
        // Fetch both liked and bookmarked content
        const [likedResponse, bookmarkedResponse] = await Promise.all([
          getLikeBookmarkedContent("liked", tatvaPediaToken),
          getLikeBookmarkedContent("bookmarked", tatvaPediaToken),
        ]);

        if (likedResponse) {
          setTatvaPediaLikedList(likedResponse);
          const likedIds = likedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalLikes(likedIds);
        }

        if (bookmarkedResponse) {
          setTatvaPediaBookmarkedList(bookmarkedResponse);
          const bookmarkedIds = bookmarkedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalBookmarks(bookmarkedIds);
        }
      } catch (error) {
        console.error("Error fetching initial state:", error);
      }
    };

    fetchInitialState();
  }, [tatvaPediaToken]);

  const fetchSearchHistory = async () => {
    if (doctorDetails?.user?.b2cId) {
      const response = await getSearchHistory(doctorDetails?.user?.b2cId);
      if (response?.data?.status === 200) {
        setSearchHistory(response.data.body.content);
      }
    }
  };

  const handleLike = async (content: any, isLiked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["like"] = true;

      // Optimistically update UI
      setLocalLikes((prev) => {
        const newLikes = isLiked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newLikes;
      });

      // Update content counts
      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? -1 : 1),
          },
        };
      });

      // Make API call
      const response = await likeContent(
        content.docId,
        content.contentTypeId,
        !isLiked,
        token
      );

      if (response) {
        // Update global state after successful API call
        updateTatvaPediaLike(content, !isLiked);

        // Fetch updated like list to ensure consistency
        const updatedLikedContent = await getLikeBookmarkedContent(
          "liked",
          token
        );
        if (updatedLikedContent) {
          setTatvaPediaLikedList(updatedLikedContent);
        }
      } else {
        // Revert local states on failure
        setLocalLikes((prev) =>
          isLiked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );

        setContentCounts((prev) => {
          const key = `${content.docId}_${content.contentTypeId}`;
          const currentCount = prev[key] || { like_count: 0 };
          return {
            ...prev,
            [key]: {
              ...currentCount,
              like_count: currentCount.like_count + (isLiked ? 1 : -1),
            },
          };
        });
      }
    } catch (error) {
      console.error("Error in like operation:", error);
      // Revert local states on error
      setLocalLikes((prev) =>
        isLiked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );

      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? 1 : -1),
          },
        };
      });
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["like"] = false;
      }, 1500);
    }
  };

  const handleBookmark = async (content: any, isBookmarked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["bookmark"] = true;

      // Optimistically update UI
      setLocalBookmarks((prev) => {
        const newBookmarks = isBookmarked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newBookmarks;
      });

      // Make API call
      const response = await bookmarkContent(
        content.docId,
        content.contentTypeId,
        !isBookmarked,
        token
      );

      if (response) {
        // Update global state after successful API call
        updateTatvaPediaBookmark(content, !isBookmarked);

        // Fetch updated bookmark list to ensure consistency
        const updatedBookmarkedContent = await getLikeBookmarkedContent(
          "bookmarked",
          token
        );
        if (updatedBookmarkedContent) {
          setTatvaPediaBookmarkedList(updatedBookmarkedContent);
        }
      } else {
        // Revert local state on failure
        setLocalBookmarks((prev) =>
          isBookmarked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );
      }
    } catch (error) {
      console.error("Error in bookmark operation:", error);
      // Revert local state on error
      setLocalBookmarks((prev) =>
        isBookmarked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["bookmark"] = false;
      }, 1500);
    }
  };

  const isItemLiked = (item: ContentItem) => {
    if (!item?.docId) return false;
    return (
      localLikes.includes(item.docId) ||
      tatvaPediaBookmarks?.likedList?.some(
        (likedItem: any) => likedItem.contentId === item.docId
      ) ||
      false
    );
  };

  const isItemBookmarked = (item: ContentItem) => {
    if (!item?.docId) return false;
    return (
      localBookmarks.includes(item.docId) ||
      tatvaPediaBookmarks?.bookmarkedList?.some(
        (bookmarkedItem: any) => bookmarkedItem.contentId === item.docId
      ) ||
      false
    );
  };

  // Helper function to get token
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");

  const fetchInitialContent = async () => {
    try {
      setIsLoading(true);
      const results = await searchContent({
        query: "", // empty query for initial content
        page: 0,
        pageSize: 20,
      });
      setSearchResults(results);

      // Fetch content counts immediately after getting search results
      if (results?.response?.docs && getToken()) {
        const contents = results.response.docs.map((doc: any) => ({
          contentId: doc.docId,
          contentTypeId: doc.contentTypeId,
        }));

        const countsResponse = await getContentCounts({
          contents,
          token: getToken(),
          userId: doctorDetails?.user?.id,
        });

        const countsMap = countsResponse.body.reduce((acc: any, item: any) => {
          acc[`${item.docId}_${item.docTypeId}`] = {
            like_count: item.likeCount,
            isExternalSharable: item.isExternalSharable,
            uploadThumbnailUrl: item.uploadThumbnailUrl,
            previewUrl: item.previewUrl,
            readTime: item.readTime,
            duration: item.duration,
          };
          return acc;
        }, {});

        setContentCounts(countsMap);
      }
    } catch (error) {
      console.error("Error fetching initial content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch initial content when component mounts
  useEffect(() => {
    fetchInitialContent();
  }, []);

  const handleTabClick = async (tab: string) => {
    setActiveTab(tab);
    setIsLoading(true);

    try {
      // Map tabs to their corresponding content type strings
      let contentType: string | string[] = "";
      
      if (tab === "latest") {
        contentType = ""; // No filter for "latest"
      } else {
        // Find the module data for this tab
        const moduleItem = moduleData.find(item => item.id.toString() === tab);
        contentType = moduleItem ? moduleItem.id.toString() : "";
      }

      // Call search API with content type filter
      const results = await searchContent({
        query: searchTerm,
        page: 0,
        pageSize: 15,
        contentType: contentType,
      });

      setSearchResults(results);
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setIsLoading(false);
    }

    // Existing scroll logic
    const tabsContainer = tabsRef.current;
    if (tabsContainer) {
      const clickedTab = tabsContainer.querySelector(`[data-tab="${tab}"]`);
      if (clickedTab) {
        const containerWidth = tabsContainer.offsetWidth;
        const tabWidth = (clickedTab as HTMLElement).offsetWidth;
        const tabLeft = (clickedTab as HTMLElement).offsetLeft;
        const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;

        tabsContainer.scrollTo({
          left: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  };

  const fetchSuggestions = async (query: string) => {
    try {
      const response = await getSuggestions(query);

      // Check if suggestions exist
      if (!response?.suggest?.ft2?.[query]?.suggestions?.length) {
        setSuggestions([]);
        setShowSuggestions(false);
        setShowNoResults(true);
        return;
      }

      const transformedSuggestions = response?.suggest?.ft2?.[
        query
      ]?.suggestions
        .map((suggestion: any) => ({
          id: suggestion.payload,
          title: suggestion.term,
        }))
        .filter(
          (suggestion: any, index: number, self: any[]) =>
            index === self.findIndex((s) => s.title === suggestion.title)
        );

      setSuggestions(transformedSuggestions);
      setShowSuggestions(true);
      setShowNoResults(false);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSuggestions([]);
      setShowSuggestions(false);
      setShowNoResults(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Force re-render on iOS WebView
    if (searchInputRef.current) {
      searchInputRef.current.style.opacity = "0.99";
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.style.opacity = "1";
        }
      }, 10);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // When search is cleared, show initial content
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setShowNoResults(false);
      setSearchResults(searchResults); // Keep existing search results
      return;
    }

    // Search with query
    if (value.length >= 3) {
      timeoutRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 500);
    }
  };

  const handleSuggestionClick = async (suggestion: Suggestion, position?: number) => {
    try {
      setIsLoading(true);
      const cleanText = suggestion.title.replace(/<\/?[^>]+(>|$)/g, "");
      setSearchTerm(cleanText);
      setShowSuggestions(false);
      setShowSearchHistory(false);
      setShowContent(true); // Set to true when suggestion is clicked

      // A tap on a dropdown entry is both a suggestion click and the search itself.
      if (position !== undefined) {
        trackZymedEvent(ZYMED_EVENTS.PEDIA_SEARCH_SUGGESTION_CLICKED, {
          suggestion_text: cleanText,
          position,
        });
      }
      trackZymedEvent(ZYMED_EVENTS.PEDIA_SEARCH_INITIATED, {
        search_origin: consumePediaSearchOrigin(),
        query_text: cleanText.trim(),
      });

      const results = await searchContent({
        query: cleanText.trim(),
        page: 0,
        pageSize: 30,
      });

      trackZymedEvent(ZYMED_EVENTS.PEDIA_SEARCH_RESULT_FETCHED, {
        query_text: cleanText.trim(),
        results_count: results?.response?.numFound ?? results?.response?.docs?.length ?? 0,
      });
      setSearchResults(results);
      storeSearchTerm({
        search_term: cleanText.trim(),
        b2c: doctorDetails?.user?.b2cId,
      });
    } catch (error) {
      console.error("Error searching content:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleSearchHistoryClick = async (query: string) => {

  // };

  // Update handleClearSearch to reset showContent
  const handleClearSearch = () => {
    setSearchTerm("");
    setSuggestions([]);
    setShowSuggestions(false);
    setShowNoResults(false);
    setShowContent(false); // Reset when search is cleared
    setSearchResults(null);
    setShowSearchHistory(true);
    fetchSearchHistory();
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search input focus
  const handleSearchFocus = () => {
    setSearchInputFocused(true);

    // Delay to ensure WebView handles focus properly
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        // Force cursor to end of input
        const length = searchInputRef.current.value.length;
        searchInputRef.current.setSelectionRange(length, length);
      }
    }, 100);

    if (searchTerm.length >= 3) {
      setShowSuggestions(true);
    }
  };

  const handleSearchBlur = () => {
    setSearchInputFocused(false);
  };

  // Keep the existing useEffect for content counts during search
  useEffect(() => {
    const fetchContentCounts = async () => {
      const authToken = getToken();
      if (!searchResults?.response?.docs || !authToken) return;

      try {
        const contents = searchResults.response.docs.map((doc: any) => ({
          contentId: doc.docId,
          contentTypeId: doc.contentTypeId,
        }));

        const countsResponse = await getContentCounts({
          contents,
          token: authToken,
          userId: doctorDetails?.user?.id,
        });

        const countsMap = countsResponse.body.reduce((acc: any, item: any) => {
          acc[`${item.docId}_${item.docTypeId}`] = {
            like_count: item.likeCount,
            isExternalSharable: item.isExternalSharable,
            uploadThumbnailUrl: item.uploadThumbnailUrl,
            previewUrl: item.previewUrl,
            readTime: item.readTime,
            duration: item.duration,
          };
          return acc;
        }, {});

        setContentCounts(countsMap);
      } catch (error) {
        console.error("Error fetching content counts:", error);
      }
    };

    fetchContentCounts();
  }, [searchResults?.response?.docs, getToken()]);

  return (
    <div className="flex flex-col h-screen">
      {/* Fixed Header Section */}
      <div className="flex-none">
        <div className="bg-white p-4 flex justify-between items-center z-10 text-2xl font-bold text-left">
          Search
        </div>

        {/* Search Input */}
        <div className="p-4 bg-white">
          <div ref={searchContainerRef} className="relative">
            <div className="relative flex items-center">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder={`Search ${getLabel("pediaLabel")}`}
                className="w-full px-4 py-2 pl-10 pr-10 bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={{
                  // iOS WebView specific fixes
                  WebkitAppearance: "none",
                  WebkitUserSelect: "text",
                  userSelect: "text",
                  transform: "translateZ(0)",
                  WebkitTransform: "translateZ(0)",
                  willChange: "contents",
                  // Force re-render when focused to show cursor
                  opacity: searchInputFocused ? 0.99 : 1,
                  // Ensure text color is visible
                  color: searchTerm ? "#374151" : "#9CA3AF", // gray-700 for text, gray-400 for placeholder
                }}
                // iOS specific attributes
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              {searchTerm && (
                <FaTimes
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  onClick={handleClearSearch}
                />
              )}
            </div>

            {showSuggestions && suggestions.length > 0 ? (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion.id || index}`}
                    className="px-4 py-3 cursor-pointer hover:bg-gray-100 flex items-center justify-between group"
                  >
                    <div
                      className="flex items-center flex-1"
                      onClick={() => handleSuggestionClick(suggestion, index + 1)}
                    >
                      <FaSearch className="text-gray-400 mr-3" />
                      <div
                        className="text-left flex-1"
                        dangerouslySetInnerHTML={{ __html: suggestion.title }}
                      />
                      <GoArrowUpLeft className="text-gray-400 w-[1.3rem] h-[1.3rem] ml-3" />
                    </div>
                    {/* <FaTimes
                                            className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newSuggestions = suggestions.filter((_, i) => i !== index);
                                                setSuggestions(newSuggestions);
                                                if (newSuggestions.length === 0) {
                                                    setShowSuggestions(false);
                                                }
                                            }}
                                        /> */}
                  </div>
                ))}
              </div>
            ) : (
              !!searchHistory?.length &&
              showSearchHistory && (
                <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg">
                  {searchHistory?.map(
                    ({ search_term, id }: any, index: number) => (
                      <div
                        key={`${id || index}`}
                        className="px-4 py-3 cursor-pointer hover:bg-gray-100 flex items-center justify-between group"
                      >
                        <div
                          className="flex items-center flex-1"
                          onClick={() =>
                            handleSuggestionClick({ title: search_term, id })
                          }
                        >
                          <FaSearch className="text-gray-400 mr-3" />
                          <div
                            className="text-left flex-1"
                            dangerouslySetInnerHTML={{ __html: search_term }}
                          />
                          <GoArrowUpLeft className="text-gray-400 w-[1.3rem] h-[1.3rem] ml-3" />
                        </div>
                      </div>
                    )
                  )}
                </div>
              )
            )}
          </div>
        </div>

        {/* Only show tabs if showContent is true */}
        {showContent && (
          <div className="shadow-md bg-white">
            <div
              ref={tabsRef}
              className="flex space-x-1 overflow-x-auto scrollbar-hide mx-2"
            >
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  data-tab={tab.id}
                  onClick={() => handleTabClick(tab.id.toString())}
                  className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${
                    activeTab === tab.id.toString() ? "text-indigo-600" : ""
                  }`}
                >
                  <span
                    className={`flex w-max items-center font-roboto text-base px-4 py-2 leading-6 tracking-[0.25px] ${
                      activeTab === tab.id.toString()
                        ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                        : ""
                    }`}
                  >
                    {/* {activeTab === tab.id.toString() && (
                      <div className="flex justify-center items-center bg-indigo-600 w-[1.1rem] h-[1.1rem] rounded-full p-1 mr-1">
                        <FaFile className="text-white" />
                      </div>
                    )} */}
                    {tab.displayName}
                  </span>
                  {activeTab === tab.id.toString() && (
                    <span className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Only show content section if showContent is true */}
      {showContent && (
        <div className="flex-1 overflow-y-auto mb-4">
          {(isLoading || isActionLoading) && (
            <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}
          <div className="px-4 mt-4 mb-20">
            {showNoResults ? (
              <div className="flex flex-col items-center h-[40vh] justify-center mt-20">
                <img
                  src={noResultsImage}
                  alt="No results"
                  className="w-32 h-32 mb-4"
                />
                <p className="text-gray-600 text-center">
                  No results found for <strong>{searchTerm}</strong>. Try
                  searching for related topics
                </p>
              </div>
            ) : (
              <div className="space-y-4 w-full">
                {searchResults?.response?.docs &&
                searchResults.response.docs.length > 0 ? (
                  searchResults.response.docs.map((item: any) => {
                    const contentCountKey = `${item.docId}_${item.contentTypeId}`;
                    const contentCount = contentCounts[contentCountKey] || {};
                    return (
                      <TpRectangleCard
                        key={item.id}
                        isShowcontentType={true}
                        isLike={isItemLiked(item)}
                        isBookmarked={isItemBookmarked(item)}
                        onLike={() => handleLike(item, isItemLiked(item))}
                        onBookmark={() =>
                          handleBookmark(item, isItemBookmarked(item))
                        }
                        data={{
                          docId: item.docId,
                          contentTitle: item.contentTitle,
                          contentType: item.contentType,
                          uploadThumbnailUrl: item.uploadThumbnailUrl,
                          contentDescription: item.contentDescription,
                          previewDeepLink: item.previewDeepLink,
                          like_count:
                            contentCount.like_count || item.like_count,
                          contentTypeId: item.contentTypeId,
                          publishedDate: item.publishedDate,
                          isExternalSharable:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.isExternalSharable || 0,
                          previewUrl:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.previewUrl || "",
                          readTime:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.readTime || "",
                          duration:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.duration || "",
                          deepLinkCode: item.deepLinkCode,
                        }}
                        isBackground={false}
                      />
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center h-[40vh] justify-center mt-20">
                    <img
                      src={noResultsImage}
                      alt="No results"
                      className="w-32 h-32 mb-4"
                    />
                    <p className="text-gray-600 text-center">
                      No results found for <strong>{searchTerm}</strong>. Try
                      searching for related topics
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <NavBar />
    </div>
  );
}

export default PediaSearch;
